<template>
  <Row>
    <i-col span="24">
      <AffixHeader>
        <span slot="current">Documents</span>
        <span slot="title">Documents</span>
      </AffixHeader>

      <template v-if="$can('create', 'documents')">
        <i-button type="primary" @click="$router.push('/documents/new')">
          <FFIcon type="plus" /> New Document
        </i-button>
        <br />
        <br />
      </template>

      <Card dis-hover>
        <h3 slot="title">Search</h3>
        <i-form :model="form" :label-width="120">
          <i-form-item label="Serial No.">
            <i-input v-model="form.serial_no"></i-input>
          </i-form-item>

          <i-form-item label="Name">
            <i-input v-model="form.name"></i-input>
          </i-form-item>

          <i-form-item label="Publication Date">
            <Tooltip
              content="Double click for a single date."
              placement="top-start"
            >
              <DatePicker
                type="daterange"
                v-model="form.publication_date"
                format="yyyy-MM-dd"
                style="width:100%;"
              ></DatePicker>
            </Tooltip>
          </i-form-item>

          <i-form-item label="Sources">
            <Row>
              <i-col span="22">
                <TagSelectPublisher
                  v-model="form.sources"
                  style="padding-right: 6px;"
                ></TagSelectPublisher>
              </i-col>
              <i-col span="2">
                <i-switch v-model="form.sourcesInclusive" size="large">
                  <span slot="open">All</span> <span slot="close">Any</span>
                </i-switch>
              </i-col>
            </Row>
          </i-form-item>

          <i-form-item label="Publishers">
            <Row>
              <i-col span="22">
                <TagSelectPublisher
                  v-model="form.publishers"
                  style="padding-right: 6px;"
                ></TagSelectPublisher>
              </i-col>
              <i-col span="2">
                <i-switch v-model="form.publishersInclusive" size="large">
                  <span slot="open">All</span> <span slot="close">Any</span>
                </i-switch>
              </i-col>
            </Row>
          </i-form-item>

          <i-form-item label="Countries">
            <Row>
              <i-col span="22">
                <TagSelectCountryByISO3
                  v-model="form.countries"
                  style="padding-right: 6px;"
                ></TagSelectCountryByISO3>
              </i-col>
              <i-col span="2">
                <i-switch v-model="form.countriesInclusive" size="large">
                  <span slot="open">All</span> <span slot="close">Any</span>
                </i-switch>
              </i-col>
            </Row>
          </i-form-item>

          <Row type="flex" justify="end" style="margin:12px 0;">
            <i-form-item class="action-buttons">
              <i-button icon="search" @click="search">Search</i-button>
              <i-button icon="close" @click="reset">Reset</i-button>
            </i-form-item>
          </Row>
        </i-form>
      </Card>

      <br />
      <DocumentsTable :conditions="conditions"></DocumentsTable>
    </i-col>
  </Row>
</template>

<script>
import moment from "moment";
import DocumentsTable from "@/documents/table";
import TagSelectPublisher from "@/components/tag_select_publisher";
import TagSelectCountryByISO3 from "@/components/tag_select_country_by_iso3";
import { mapGetters, mapMutations } from "vuex";
import { getTsQueryValue } from "@/utils/common";

const emptyForm = {
  serial_no: "",
  name: "",
  publication_date: [],
  sources: [],
  sourcesInclusive: true,
  publishers: [],
  publishersInclusive: true,
  countries: [],
  countriesInclusive: true,
};

export default {
  components: {
    DocumentsTable,
    TagSelectPublisher,
    TagSelectCountryByISO3,
  },
  data() {
    return {
      form: Object.assign({}, emptyForm),
      conditions: {},
    };
  },
  created() {
    if (this.searches["documents"]) {
      this.form = this.searches["documents"];
      this.search();
    }
  },
  computed: {
    ...mapGetters(["searches"]),
  },
  methods: {
    ...mapMutations(["setSearch"]),
    search() {
      let conditions = {};

      // Serial No.
      if (this.form.serial_no !== "") {
        conditions["serial_no"] = `eq.${this.form.serial_no.trim()}`;
      }

      // Name
      if (this.form.name !== "") {
        conditions["name"] = getTsQueryValue(this.form.name)
      }

      if (this.form.publication_date.length > 0) {
        const start = moment(this.form.publication_date[0]);
        const end = moment(this.form.publication_date[1]);
        if (start.isValid() && end.isValid()) {
          conditions["and"] = `(publication_date.gte.'${start.format(
            "YYYY-MM-DD"
          )}',publication_date.lte.'${end.format("YYYY-MM-DD")}')`;
        }
      }

      // Sources
      if (this.form.sources.length > 0) {
        if (this.form.sourcesInclusive) {
          conditions["sources_tags"] =
            "cs." + JSON.stringify(this.form.sources.map(i => i.name));
        } else {
          conditions["or"] = arrayOrCondition(
            "sources",
            this.form.sources.map(i => i.name)
          );
        }
      }

      // Publishers
      if (this.form.publishers.length > 0) {
        if (this.form.publishersInclusive) {
          conditions["publishers_tags"] =
            "cs." + JSON.stringify(this.form.publishers.map(i => i.name));
        } else {
          conditions["or"] = arrayOrCondition(
            "publishers",
            this.form.publishers.map(i => i.name)
          );
        }
      }

      // Countries
      if (this.form.countries.length > 0) {
        if (this.form.countriesInclusive) {
          conditions["countries"] = "cs." + JSON.stringify(this.form.countries);
        } else {
          conditions["or"] = arrayOrCondition("countries", this.form.countries);
        }
      }

      this.conditions = conditions;
      this.setSearch({ type: "documents", data: this.form });
    },

    reset() {
      this.form = Object.assign({ countries: [] }, emptyForm);
      this.conditions = {};
      this.setSearch({ type: "documents", data: null });
    },
  },
};

function arrayOrCondition(field, values) {
  let result = "(";
  for (let value of values) {
    result += field + ".cs." + JSON.stringify([value]) + ",";
  }
  result = result.slice(0, -1);
  result += ")";
  return result;
}
</script>

<style scopped>
.ivu-tooltip {
  width: 100%;
}
.ivu-tooltip-rel {
  width: 100%;
}
</style>
